import { X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

const INITIAL_FORM = {
  message: '',
  employeeToMeet: '',
  mobileNumber: '',
  plotOrBuilding: '',
  securityPin: '',
  insiderConfirmed: false,
};

const VisitorCheckInOverlay = ({ isOpen, onClose, data }) => {
  const [form, setForm] = useState(INITIAL_FORM);

  const initialValues = useMemo(() => {
    const candidate = data?.initialValues;
    return candidate && typeof candidate === 'object' ? candidate : null;
  }, [data?.initialValues]);

  useEffect(() => {
    if (!isOpen) return;
    setForm({ ...INITIAL_FORM, ...(initialValues || {}) });
  }, [isOpen, initialValues]);

  if (!isOpen) return null;

  const handleClose = () => {
    setForm(INITIAL_FORM);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2 sm:p-4 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-label="Visitor details"
      onClick={handleClose}
    >
      <div
        className="w-full max-w-[560px] relative bg-neutral-50/50 rounded-xl shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20px_0px_rgba(255,255,255,0.10)] shadow-[inset_0px_4px_25px_0px_rgba(255,255,255,0.35)] outline outline-1 outline-offset-[-1px] outline-neutral-300/20 backdrop-blur-[30px] overflow-hidden flex flex-col my-auto"
        onClick={event => event.stopPropagation()}
      >
        <div className="px-6 sm:px-8 pt-4 sm:pt-6 pb-3 sm:pb-4 flex items-start justify-between">
          <h3 className="text-3xl font-semibold text-neutral-900">
            {data?.title || 'Visitor Check-in'}
          </h3>
          <button
            type="button"
            onClick={handleClose}
            className="w-7 h-7 rounded-md bg-neutral-300/70 text-neutral-600 hover:bg-neutral-300 inline-flex items-center justify-center"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form
          className="px-4 sm:px-6 md:px-8 pb-5"
          onSubmit={event => {
            event.preventDefault();

            const isMobileValid = /^\d{10,15}$/.test(form.mobileNumber);
            const isPinValid = form.insiderConfirmed ? /^\d{4,8}$/.test(form.securityPin) : true;
            const isFormValid =
              form.message.trim() &&
              form.employeeToMeet.trim() &&
              isMobileValid &&
              form.plotOrBuilding.trim() &&
              isPinValid &&
              form.insiderConfirmed;

            if (!isFormValid) return;

            if (typeof data?.onSubmit === 'function') {
              data.onSubmit(form);
            }

            handleClose();
          }}
        >
          <div className="rounded-lg border border-cyan-100 bg-cyan-50/60 p-3">
            <label className="block text-sm font-semibold text-neutral-900 mb-1">Purpose</label>
            <textarea
              value={form.message}
              onChange={e => setForm(prev => ({ ...prev, message: e.target.value }))}
              placeholder="Write a short message..."
              rows={3}
              className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-700 resize-none"
              required
            />

            <div className="mt-3">
              <label className="block text-sm font-semibold text-neutral-900 mb-1">
                Employee to meet
              </label>
              <input
                value={form.employeeToMeet}
                onChange={e => setForm(prev => ({ ...prev, employeeToMeet: e.target.value }))}
                placeholder="Employee name"
                className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-700"
                required
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              <div>
                <label className="block text-sm font-semibold text-neutral-900 mb-1">
                  Mobile number
                </label>
                <input
                  value={form.mobileNumber}
                  onChange={e => {
                    const nextValue = e.target.value.replace(/\D/g, '');
                    setForm(prev => ({ ...prev, mobileNumber: nextValue }));
                  }}
                  inputMode="numeric"
                  pattern="\\d{10,15}"
                  maxLength={15}
                  placeholder="10–15 digits"
                  className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-700"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-neutral-900 mb-1">
                  Plot / building
                </label>
                <input
                  value={form.plotOrBuilding}
                  onChange={e => setForm(prev => ({ ...prev, plotOrBuilding: e.target.value }))}
                  placeholder="Plot / building name or number"
                  className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-700"
                  required
                />
              </div>
            </div>

            <label className="mt-3 flex items-start gap-2 text-sm text-neutral-800 select-none">
              <input
                type="checkbox"
                checked={form.insiderConfirmed}
                onChange={e =>
                  setForm(prev => ({
                    ...prev,
                    insiderConfirmed: e.target.checked,
                    securityPin: e.target.checked ? prev.securityPin : '',
                  }))
                }
                className="mt-0.5 h-4 w-4 rounded border-neutral-300"
                required
              />
              Confirmed from the insider
            </label>

            {form.insiderConfirmed ? (
              <div className="mt-3">
                <label className="block text-sm font-semibold text-neutral-900 mb-1">
                  Security PIN
                </label>
                <input
                  value={form.securityPin}
                  onChange={e => {
                    const nextValue = e.target.value.replace(/\D/g, '');
                    setForm(prev => ({ ...prev, securityPin: nextValue }));
                  }}
                  inputMode="numeric"
                  pattern="\\d{4,8}"
                  maxLength={8}
                  placeholder="4–8 digits"
                  className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-700"
                  required
                />
                <p className="mt-1 text-[11px] text-neutral-600">
                  Enter a 4–8 digit PIN shared with the insider.
                </p>
              </div>
            ) : null}

            <div className="mt-4 pt-3 border-t border-gray-200 flex justify-center">
              <button
                type="submit"
                className="inline-flex items-center justify-center rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 text-white !text-white px-6 py-2.5 text-sm font-semibold shadow-sm transition hover:from-green-700 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-400/50 focus:ring-offset-2 active:scale-[0.99]"
              >
                Submit
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VisitorCheckInOverlay;

